/**
 * Stytch Service
 * Handles Stytch authentication and user management
 */

import { stytchConfig } from '../../config/stytch';
import { getStytchClient } from './stytchClient';
import type { UserMigrationStatus } from '../../stytch';

class StytchService {
  private get client() {
    return getStytchClient();
  }
  
  /**
   * Check if a user exists in Stytch by attempting to send a magic link
   * This is a workaround since Stytch doesn't have a direct "user exists" endpoint
   */
  async checkUserExists(email: string): Promise<boolean> {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    try {
      // Try to send a magic link - this will fail if user doesn't exist
      await this.client.magicLinks.email.send(email, {
        login_magic_link_url: stytchConfig.magicLinkOptions.loginMagicLinkURL,
        login_expiration_minutes: stytchConfig.magicLinkOptions.loginExpirationMinutes,
      });
      return true;
    } catch (error: any) {
      // If error is about user not found, return false
      if (error.message?.includes('not found') || error.status_code === 404) {
        return false;
      }
      // For other errors, re-throw
      throw error;
    }
  }
  
  /**
   * Get user migration status
   */
  async getUserMigrationStatus(email: string): Promise<UserMigrationStatus> {
    const existsInStytch = await this.checkUserExists(email);
    
    // Check if user exists in Xano
    let existsInXano = false;
    let xanoUserId: string | undefined;
    
    try {
      // This will be implemented when we have the Xano check
      const xanoResponse = await fetch(`${import.meta.env.VITE_XANO_API_URL}/user/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      if (xanoResponse.ok) {
        const data = await xanoResponse.json();
        existsInXano = data.exists;
        xanoUserId = data.userId;
      }
    } catch (error) {
      console.error('Failed to check Xano user:', error);
    }
    
    return {
      existsInStytch,
      existsInXano,
      needsPasswordSet: !existsInStytch && existsInXano,
      xanoUserId,
    };
  }
  
  /**
   * Get session token for Xano exchange
   */
  getSessionToken(): string | null {
    // Session tokens are returned in the authentication response, not stored in the session
    return null;
  }
  
  /**
   * Get session JWT for Xano exchange
   */
  getSessionJWT(): string | null {
    // Session JWTs are returned in the authentication response, not stored in the session
    return null;
  }
  
  /**
   * Start OAuth flow
   */
  async startOAuthFlow(provider: 'google' | 'microsoft' | 'apple') {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    return this.client.oauth[provider].start({
      login_redirect_url: `${window.location.origin}/auth/oauth-callback`,
      signup_redirect_url: `${window.location.origin}/auth/oauth-callback`,
    });
  }
  
  /**
   * Authenticate OAuth token
   */
  async authenticateOAuth(token: string) {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    const response = await this.client.oauth.authenticate(token, {
      session_duration_minutes: stytchConfig.sessionOptions.sessionDurationMinutes,
    });
    
    console.log('Stytch OAuth Authentication Response:', {
      user: response.user,
      session: response.session,
      session_token: response.session_token,
      session_jwt: response.session_jwt,
      user_id: response.user.user_id,
      email: response.user.emails[0]?.email,
      provider: response.provider_type,
      provider_values: response.provider_values,
    });
    
    return response;
  }
  
  /**
   * Send magic link
   */
  async sendMagicLink(email: string, isSignup: boolean = false) {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    if (isSignup) {
      return this.client.magicLinks.email.loginOrCreate(email, {
        login_magic_link_url: stytchConfig.magicLinkOptions.loginMagicLinkURL,
        signup_magic_link_url: stytchConfig.magicLinkOptions.signupMagicLinkURL,
        login_expiration_minutes: stytchConfig.magicLinkOptions.loginExpirationMinutes,
        signup_expiration_minutes: stytchConfig.magicLinkOptions.signupExpirationMinutes,
      });
    } else {
      return this.client.magicLinks.email.send(email, {
        login_magic_link_url: stytchConfig.magicLinkOptions.loginMagicLinkURL,
        login_expiration_minutes: stytchConfig.magicLinkOptions.loginExpirationMinutes,
      });
    }
  }
  
  /**
   * Authenticate magic link token
   */
  async authenticateMagicLink(token: string) {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }

    const response = await this.client.magicLinks.authenticate(token, {
      session_duration_minutes: stytchConfig.sessionOptions.sessionDurationMinutes,
    });

    console.log('Stytch Magic Link Authentication Response:', {
      user: response.user,
      session: response.session,
      session_token: response.session_token,
      session_jwt: response.session_jwt,
      user_id: response.user.user_id,
      email: response.user.emails[0]?.email,
    });

    return response;
  }

  /**
   * Authenticate with existing session JWT (for SSO from Aksanio)
   * NOTE: This method is not used - SSO goes directly through Xano's sso-verify endpoint.
   * The Stytch client SDK doesn't support session JWT authentication like the server SDK.
   * @deprecated Use Xano's sso-verify endpoint instead (see SSOCallback.tsx)
   */
  async authenticateSessionJWT(_sessionJWT: string): Promise<never> {
    throw new Error(
      'authenticateSessionJWT is not supported on client SDK. Use Xano sso-verify endpoint instead.'
    );
  }
  
  /**
   * Get current session
   */
  getSession() {
    if (!this.client) {
      return null;
    }
    
    return this.client.session.getSync();
  }
  
  /**
   * Get current user
   */
  getUser() {
    if (!this.client) {
      return null;
    }
    
    return this.client.user.getSync();
  }
  
  /**
   * Update user name
   */
  async updateUserName(firstName: string, lastName: string) {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    try {
      // Note: The Stytch frontend SDK may not support user updates directly
      // This would typically be done through a backend API call
      // For now, we'll store this in the session and pass it to Xano
      const user = this.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      // Store name data to be sent to Xano
      sessionStorage.setItem('userNameUpdate', JSON.stringify({
        firstName,
        lastName,
        userId: user.user_id
      }));
      
      return { success: true };
    } catch (error) {
      console.error('Failed to update user name:', error);
      throw error;
    }
  }

  /**
   * Revoke session (logout)
   */
  async revokeSession() {
    if (!this.client) {
      throw new Error('Stytch client not initialized');
    }
    
    return this.client.session.revoke();
  }
  
  /**
   * Listen to session changes
   */
  onSessionChange(callback: (session: any) => void) {
    if (!this.client) {
      console.warn('Stytch client not initialized');
      return () => {};
    }
    
    return this.client.session.onChange(callback);
  }
  
  /**
   * Listen to user changes
   */
  onUserChange(callback: (user: any) => void) {
    if (!this.client) {
      console.warn('Stytch client not initialized');
      return () => {};
    }
    
    return this.client.user.onChange(callback);
  }
  
  /**
   * Check if email is allowed to signup
   */
  isEmailAllowedForSignup(email: string): boolean {
    // Get allowed domains from environment or config
    const allowedDomains = import.meta.env.VITE_ALLOWED_SIGNUP_DOMAINS?.split(',') || [];
    
    // If no domains specified, allow all (for development)
    if (allowedDomains.length === 0) {
      console.warn('No signup domain restrictions configured');
      return true;
    }
    
    const emailDomain = email.split('@')[1];
    return allowedDomains.includes(emailDomain);
  }
}

// Export singleton instance
export const stytchService = new StytchService();